import { Fragment, MouseEvent, PropsWithChildren } from 'react';
import { styled, css } from 'styled-components';
import { SubtitleM, SubtitleS } from '@/core/components/Typography.ts';
import Icon from '@/core/components/Icon.tsx';

export type PrimaryButtonColor = 'yellow' | 'blue' | 'dark-blue';
export type PrimaryButtonSize = 'small' | 'large';

export type PrimaryButtonProps = PropsWithChildren<{
	color?: PrimaryButtonColor;
	disabled?: boolean;
	size?: PrimaryButtonSize;
	icon?: string; // optional CSS class for an icon sprite
	href?: string;
	dataQaId?: string;
	onClick?: (event: MouseEvent<HTMLButtonElement | HTMLAnchorElement>) => void;
	className?: string;
	type?: 'button' | 'submit' | 'reset';
	id?: string;
}>;

const colorMap = {
	yellow: {
		background: 'var(--Primary-cta-yellow)',
		text: 'var(--Primary-squla-dark-blue)',
		boxShadow: 'var(--Yellow-80)',
	},
	blue: {
		background: 'var(--Primary-cta-blue)',
		text: 'var(--Primary-squla-dark-blue)',
		boxShadow: 'var(--Blue-80)',
	},
	'dark-blue': {
		background: 'var(--Primary-cta-dark-blue)',
		text: 'var(--White)',
		boxShadow: 'var(--Dark-Blue-80)',
	},
};

// Host pages (WordPress, the parent app) ship their own paragraph margins; the label must not
// inherit them or it sits off-centre.
const Label = styled.span`
	display: inline-flex;
	align-items: center;

	p {
		margin: 0;
	}
`;

const BaseButton = styled.button<{
	$color: PrimaryButtonColor;
	$size: PrimaryButtonSize;
	$disabled: boolean;
}>`
	display: flex;
	align-items: center;
	justify-content: center;
	font-weight: 800;
	text-decoration: none;
	cursor: pointer;
	border-radius: 40px;
	border: 0;
	margin-top: 0;
	gap: 8px;
	transition: transform 120ms ease;
	height: 48px;
	font-size: 16px;
	line-height: 20px;
	padding: 12px 32px;
	background-color: ${(p) => colorMap[p.$color].background};
	color: ${(p) => colorMap[p.$color].text};
	box-shadow: 0 -4px 0 0 ${(p) => colorMap[p.$color].boxShadow} inset;

	&:hover {
		transform: translateY(-2px);
		box-shadow: 0 -6px 0 0 ${(p) => colorMap[p.$color].boxShadow} inset;
		height: 50px;
		margin-top: -2px;
	}
	&:active {
		transform: translateY(0);
		box-shadow: 0 2px 0 0 ${(p) => colorMap[p.$color].boxShadow} inset;
		height: 48px;
		margin-top: 0;
		margin-bottom: 0;
	}

	${(p) =>
		p.$disabled &&
		css`
			cursor: default;
			color: var(--Grey-100, #7c7c9c);
			background-color: var(--Grey-40, #e9e9f1);
			box-shadow: none;
			pointer-events: none;
			&:hover,
			&:active {
				cursor: default;
				color: var(--Grey-100, #7c7c9c);
				background-color: var(--Grey-40, #e9e9f1);
				box-shadow: none;
				pointer-events: none;
				transform: none;
			}
		`}
`;

export function PrimaryButton({
	color = 'yellow',
	size = 'large',
	disabled = false,
	icon,
	href,
	dataQaId,
	children,
	onClick,
	className,
	type,
}: PrimaryButtonProps) {
	const commonProps = {
		'data-qa-id': dataQaId,
		className,
		onClick: (e: MouseEvent<HTMLButtonElement & HTMLAnchorElement>) => {
			if (disabled) {
				e.preventDefault();
				return;
			}
			onClick?.(e);
		},
	} as const;

	const content = (
		<Fragment>
			{icon ? <Icon name={icon} /> : null}
			<Label>
				{size === 'large' ? <SubtitleM>{children}</SubtitleM> : <SubtitleS>{children}</SubtitleS>}
			</Label>
		</Fragment>
	);

	if (href) {
		return (
			<BaseButton
				as="a"
				href={href}
				$color={color}
				$size={size}
				$disabled={disabled}
				{...commonProps}
			>
				{content}
			</BaseButton>
		);
	}

	return (
		<BaseButton
			type={type ?? 'button'}
			$color={color}
			$size={size}
			$disabled={disabled}
			{...commonProps}
			disabled={disabled}
		>
			{content}
		</BaseButton>
	);
}

export default PrimaryButton;
